import { useRef, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Play, X, MapPin, Volume2, VolumeX } from 'lucide-react';

const Showroom = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isMuted, setIsMuted] = useState(true);

  const videos = [
    {
      id: 1,
      title: 'Showroom Entrance',
      url: 'https://customer-assets.emergentagent.com/job_build-preview-73/artifacts/3vqxdjqb_WhatsApp%20Video%202026-04-26%20at%204.36.35%20AM.mp4',
      thumbnail: null,
    },
    {
      id: 2,
      title: 'Glass Display Area',
      url: 'https://customer-assets.emergentagent.com/job_build-preview-73/artifacts/pw9vmhpz_WhatsApp%20Video%202026-04-26%20at%204.47.27%20AM.mp4',
      thumbnail: null,
    },
    {
      id: 3,
      title: 'Aluminium Samples',
      url: 'https://customer-assets.emergentagent.com/job_build-preview-73/artifacts/28kakc0n_WhatsApp%20Video%202026-04-26%20at%204.47.33%20AM.mp4',
      thumbnail: null,
    },
    {
      id: 4,
      title: 'Product Showcase',
      url: 'https://customer-assets.emergentagent.com/job_build-preview-73/artifacts/05umvcfv_WhatsApp%20Video%202026-04-26%20at%204.47.33%20AM%20%281%29.mp4',
      thumbnail: null,
    },
    {
      id: 5,
      title: 'Showroom Tour',
      url: 'https://customer-assets.emergentagent.com/job_build-preview-73/artifacts/f413imgl_WhatsApp%20Video%202026-04-26%20at%204.47.43%20AM%20%281%29.mp4',
      thumbnail: null,
    },
  ];

  return (
    <section 
      id="showroom" 
      className="py-24 md:py-32 px-6 relative overflow-hidden"
      data-testid="showroom-section"
      ref={ref}
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-sky-50/80 via-white/60 to-sky-50/80"></div>
      
      {/* Decorative elements */}
      <div className="absolute top-20 right-0 w-96 h-96 bg-gradient-to-br from-cyan-200/30 to-sky-200/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-0 w-80 h-80 bg-gradient-to-tr from-emerald-200/20 to-cyan-200/20 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <motion.span 
            className="text-xs tracking-[0.2em] uppercase font-semibold text-[#25D366] block mb-4"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Visit Our Showroom
          </motion.span>
          <motion.h2 
            className="text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight text-slate-800 mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            Al Quoz Showroom
          </motion.h2>
          
          {/* Location Badge */}
          <motion.div 
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full border border-sky-200/50 shadow-sm"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <MapPin size={16} className="text-[#25D366]" />
            <span className="text-slate-600 text-sm font-medium">Al Quoz Industrial Area, Dubai, UAE</span>
          </motion.div>

          <motion.p 
            className="text-slate-600 max-w-2xl mx-auto text-lg mt-6"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            Experience our premium aluminium and glass products firsthand. Visit our showroom to see the quality and craftsmanship that sets us apart.
          </motion.p>
        </motion.div>

        {/* Video Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {videos.map((video, index) => (
            <motion.div
              key={video.id}
              className="relative group cursor-pointer"
              data-testid={`showroom-video-${video.id}`}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
              onClick={() => setSelectedVideo(video)}
            >
              <div className="relative aspect-video rounded-2xl overflow-hidden bg-slate-900 shadow-lg">
                {/* Video preview with autoplay muted */}
                <video
                  src={video.url}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  onMouseEnter={(e) => e.target.play()}
                  onMouseLeave={(e) => {
                    e.target.pause();
                    e.target.currentTime = 0;
                  }}
                />
                
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-300"></div>
                
                {/* Play button */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <motion.div 
                    className="w-16 h-16 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform duration-300"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Play size={28} className="text-[#25D366] ml-1" fill="#25D366" />
                  </motion.div>
                </div>

                {/* Video title */}
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="text-white font-medium text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {video.title}
                  </h3>
                </div>

                {/* Glowing border on hover */}
                <div className="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-[#25D366]/50 transition-colors duration-300"></div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div 
          className="mt-12 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.8 }}
        >
          <a
            href="https://wa.me/971566832840?text=Hello%2C%20I%20would%20like%20to%20visit%20your%20showroom%20in%20Al%20Quoz"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-whatsapp inline-flex group"
            data-testid="showroom-visit-btn"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Book a Showroom Visit
            <motion.span
              animate={{ x: [0, 4, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              →
            </motion.span>
          </a>
        </motion.div>
      </div>

      {/* Video Lightbox/Modal */}
      <AnimatePresence>
        {selectedVideo && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/95"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedVideo(null)}
            data-testid="video-lightbox"
          >
            {/* Close button */}
            <button
              className="absolute top-6 right-6 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-colors"
              onClick={() => setSelectedVideo(null)}
              data-testid="video-lightbox-close"
            >
              <X size={24} />
            </button>

            {/* Mute/Unmute button */}
            <button
              className="absolute top-6 left-6 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                setIsMuted(!isMuted);
              }}
              data-testid="video-mute-toggle"
            >
              {isMuted ? <VolumeX size={24} /> : <Volume2 size={24} />}
            </button>

            {/* Video container */}
            <motion.div
              className="relative w-full max-w-3xl"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-slate-900">
                <video
                  src={selectedVideo.url}
                  className="w-full aspect-video"
                  controls
                  autoPlay
                  muted={isMuted}
                  playsInline
                />
              </div>
              
              {/* Video title below */}
              <div className="mt-4 text-center">
                <h3 
                  className="text-xl font-medium text-white"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {selectedVideo.title}
                </h3>
                <p className="text-sky-300 text-sm mt-1">Al Quoz Showroom, Dubai</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Showroom;
